import { useApi } from '@/@diffrag/hooks/useApi'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { api } from '@/api'
import dayjs from 'dayjs'
import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { TableFormatter } from '@/@diffrag/table/TableFormatter'
import { IAdminPromotionRO, StatusEnum3, TypeEnum7 } from 'api'
import { DateInput } from '@/@diffrag/form/inputs/DateInput'
import { useNavigate } from 'react-router-dom'

export default function Promotion() {
  const page = useQueryValue('page', 1)
  const q = useQueryValue('q', undefined)
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [selectedCategories, setSelectedCategories] = useState<number[]>([])
  const [selectedRegions, setSelectedRegions] = useState<number[]>([])
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [selectedStartDate, setSelectedStartDate] = useState<Date | undefined>()
  const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>()
  const navigate = useNavigate()
  // 카테고리 목록 가져오기
  const { data: categoryData } = useApi({
    id: 'categoryList',
    fn: api().adminCategory.adminCategoryList,
  })

  // 지역 목록 가져오기
  const { data: regionData } = useApi({
    id: 'regionList',
    fn: api().adminRegion.adminRegionList,
  })

  const { data, isFetching, refetch } = useApi({
    id: 'promotionList',
    fn: api().adminPromotion.adminPromotionList,
    params: {
      categoryIds: selectedCategories,
      regionIds: selectedRegions,
      status: selectedStatuses as unknown as StatusEnum3[],
      type: selectedTypes as unknown as TypeEnum7[],
      page,
      startDate: selectedStartDate
        ? dayjs(selectedStartDate).format('YYYY-MM-DD')
        : undefined,
      endDate: selectedEndDate
        ? dayjs(selectedEndDate).format('YYYY-MM-DD')
        : undefined,
      q,
    },
  })

  const typeOptions = [
    { value: 'EVENT', label: '이벤트' },
    { value: 'LIMITED', label: '한정프로모션' },
    { value: 'MEDICAL_PACKAGE', label: '메디컬패키지' },
  ]

  const statusOptions = [
    { value: 'BEFORE_START', label: '시작 전' },
    { value: 'ONGOING', label: '진행 중' },
    { value: 'ENDED', label: '종료' },
  ]

  const selectedTypeLabels = useMemo(() => {
    if (selectedTypes.length === 0) {
      return '전체'
    }
    return typeOptions
      .filter((option) => selectedTypes.includes(option.value))
      .map((option) => option.label)
      .join(', ')
  }, [selectedTypes])

  const selectedCategoryLabels = useMemo(() => {
    if (selectedCategories.length === 0) {
      return '전체'
    }
    return (
      categoryData?.items
        ?.filter((item) => selectedCategories.includes(item.id))
        .map((item) => item.name)
        .join(', ') || '전체'
    )
  }, [selectedCategories, categoryData])

  const selectedRegionLabels = useMemo(() => {
    if (selectedRegions.length === 0) {
      return '전체'
    }
    return (
      regionData?.items
        ?.filter((item) => selectedRegions.includes(item.id))
        .map((item) => item.name)
        .join(', ') || '전체'
    )
  }, [selectedRegions, regionData])

  const selectedStatusLabels = useMemo(() => {
    if (selectedStatuses.length === 0) {
      return '전체'
    }
    return statusOptions
      .filter((option) => selectedStatuses.includes(option.value))
      .map((option) => option.label)
      .join(', ')
  }, [selectedStatuses])

  const handleTypeToggle = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    )
  }

  const handleStatusToggle = (status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    )
  }

  const handleCategoryToggle = (categoryId: number) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId],
    )
  }

  const handleRegionToggle = (regionId: number) => {
    setSelectedRegions((prev) =>
      prev.includes(regionId)
        ? prev.filter((id) => id !== regionId)
        : [...prev, regionId],
    )
  }

  const onDelete = async (rows: IAdminPromotionRO[]) => {
    await Promise.all(
      rows.map((row) => api().adminPromotion.finishUpdate(row.id)),
    )
    refetch()
  }

  return (
    <ContentView>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">타입 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedTypeLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[150px]">
              {typeOptions.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={selectedTypes.includes(option.value)}
                  onCheckedChange={() => handleTypeToggle(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">카테고리 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedCategoryLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[150px]">
              {categoryData?.items?.map((category) => (
                <DropdownMenuCheckboxItem
                  key={category.id}
                  checked={selectedCategories.includes(category.id)}
                  onCheckedChange={() => handleCategoryToggle(category.id)}
                >
                  {category.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">지역 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedRegionLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[150px]">
              {regionData?.items?.map((region) => (
                <DropdownMenuCheckboxItem
                  key={region.id}
                  checked={selectedRegions.includes(region.id)}
                  onCheckedChange={() => handleRegionToggle(region.id)}
                >
                  {region.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">상태 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedStatusLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[150px]">
              {statusOptions.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={selectedStatuses.includes(option.value)}
                  onCheckedChange={() => handleStatusToggle(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex w-full max-w-[200px] shrink-0 items-center gap-2">
          <span className="shrink-0 text-sm font-medium">시작일:</span>
          <DateInput
            value={selectedStartDate}
            onChange={setSelectedStartDate}
          />
        </div>
        <div className="flex w-full max-w-[200px] shrink-0 items-center gap-2">
          <span className="shrink-0 text-sm font-medium">종료일:</span>
          <DateInput value={selectedEndDate} onChange={setSelectedEndDate} />
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setSelectedStartDate(undefined)
            setSelectedEndDate(undefined)
          }}
        >
          시작/종료일 초기화
        </Button>
      </div>
      <SimpleTable
        createHref="/promotion/edit"
        totalCount={data?.total}
        options={{
          disableSort: true,
        }}
        onRowClick={(row) => {
          navigate(`/promotion/edit?id=${row.id}`)
        }}
        deleteText="프로모션 종료"
        onDelete={onDelete}
        columns={[
          {
            label: 'NO',
            key: 'id',
            isPrimary: true,
          },
          {
            label: '카테고리',
            key: 'category.name',
          },
          {
            label: '타입',
            key: 'type',
            formatter(value, rawValue) {
              switch (value) {
                case 'EVENT':
                  return '이벤트'
                case 'LIMITED':
                  return '한정프로모션'
                case 'MEDICAL_PACKAGE':
                  return '메디컬패키지'
                default:
                  return '-'
              }
            },
          },
          {
            label: '지역',
            key: 'region.name',
          },
          {
            label: '병원명',
            key: 'clinic.name',
          },
          {
            label: '프로모션명',
            key: 'title',
            isPrimary: true,
            formatter: (value, row) => {
              const titles = [
                { lang: 'ko', title: row.title, label: '한국어' },
                { lang: 'zh-CN', title: row.titleZhcn, label: '简体中文' },
                { lang: 'zh-TW', title: row.titleZhtw, label: '繁體中文' },
                { lang: 'en', title: row.titleEn, label: 'English' },
                { lang: 'ja', title: row.titleJa, label: '日本語' },
                { lang: 'th', title: row.titleTh, label: 'ไทย' },
              ].filter((item) => item.title) // 값이 있는 것만 필터링

              if (titles.length === 0) return '-'
              if (titles.length === 1) return titles[0].title

              return (
                <div className="space-y-1">
                  {titles.map((item) => (
                    <div key={item.lang} className="text-xs">
                      <span className="font-semibold text-blue-600">
                        [{item.label}]
                      </span>{' '}
                      {item.title}
                    </div>
                  ))}
                </div>
              )
            },
          },
          {
            label: '시술분야',
            key: 'hashtags',
            formatter: (value, row) => {
              if (!row.hashtags || row.hashtags.length === 0) return '-'

              // 언어별로 그룹화
              const languageMap: Record<string, string> = {
                ko: '한국어',
                'zh-CN': '简体中文',
                'zh-TW': '繁體中文',
                en: 'English',
                ja: '日本語',
                th: 'ไทย',
              }

              const groupedByLanguage = row.hashtags.reduce(
                (acc, hashtag) => {
                  const language = hashtag.language || 'ko'
                  const languageName = languageMap[language] || language
                  if (!acc[languageName]) {
                    acc[languageName] = []
                  }
                  acc[languageName].push(hashtag.hashtag)
                  return acc
                },
                {} as Record<string, string[]>,
              )

              return (
                <div className="space-y-1">
                  {Object.entries(groupedByLanguage).map(
                    ([language, hashtags]) => (
                      <div key={language} className="text-xs">
                        <span className="font-semibold text-blue-600">
                          [{language}]
                        </span>{' '}
                        {hashtags.map((tag) => `#${tag}`).join(' ')}
                      </div>
                    ),
                  )}
                </div>
              )
            },
          },
          {
            label: '신청자수',
            key: 'participantCount',
            formatter: (value) => (value ? value : '-'),
          },
          {
            label: '상태',
            key: 'status',
            valueMap: {
              BEFORE_START: '시작 전',
              ONGOING: '진행 중',
              ENDED: '종료',
            },
          },
          {
            label: '시작일',
            key: 'startDate',
            formatter: TableFormatter.datetime(),
          },
          {
            label: '종료일',
            key: 'endDate',
            formatter: TableFormatter.datetime(),
          },
        ]}
        data={data?.items}
        isLoading={isFetching}
      />
    </ContentView>
  )
}
